import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { applicationService, productService } from '@/services/storage';
import type { Application, Product } from '@/types';

export default function Applications() {
  const [searchParams] = useSearchParams();
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);

  const appId = searchParams.get('id');

  useEffect(() => {
    const loadApps = async () => {
      const apps = await applicationService.getAll();
      setApplications(apps);

      if (appId) {
        const app = apps.find((a) => a.id === appId);
        if (app) {
          setSelectedApp(app);
          // Get related products based on application keywords
          const allProducts = await productService.getAll();
          const related = allProducts.filter((p) => {
            const appParam = p.parameters.application;
            return typeof appParam === 'string' && appParam.toLowerCase().includes(app.titleEn.toLowerCase());
          });
          setRelatedProducts(related.slice(0, 6));
        }
      } else {
        setSelectedApp(null);
      }
    };
    loadApps();
  }, [appId]);

  return (
    <div className="pt-20">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              应用领域
            </h1>
            <p className="text-xl text-blue-100">
              瀚昕微产品广泛应用于各类电子设备
            </p>
          </div>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {!selectedApp ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {applications.map((app) => (
                <Link key={app.id} to={`/applications?id=${app.id}`}>
                  <Card className="h-full hover:shadow-xl transition-shadow group overflow-hidden">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={app.image || `https://images.unsplash.com/photo-${
                          app.id === '1'
                            ? '1496181133206-80ce9b88a853'
                            : app.id === '2'
                            ? '1581091226825-a6a2a5aee158'
                            : app.id === '3'
                            ? '1504148451-1488729e17f8'
                            : app.id === '4'
                            ? '1557597774-9d273605dfa9'
                            : '1451187580459-43490279c0fa'
                        }?w=400&q=80`}
                        alt={app.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">
                          {app.title}
                        </h3>
                        <Badge variant="outline">{app.titleEn}</Badge>
                      </div>
                      <p className="text-gray-600 mb-4">{app.description}</p>
                      <div className="flex items-center text-blue-600 font-medium">
                        了解详情 <ArrowRight className="ml-2 h-5 w-5" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div>
              {/* Back Button */}
              <Link
                to="/applications"
                className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-8"
              >
                <ArrowRight className="mr-2 h-5 w-5 rotate-180" />
                返回应用领域
              </Link>

              {/* Application Detail */}
              <div className="grid lg:grid-cols-2 gap-12 mb-12">
                <div>
                  <Badge className="mb-4">{selectedApp.titleEn}</Badge>
                  <h2 className="text-4xl font-bold text-gray-900 mb-6">
                    {selectedApp.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    {selectedApp.description}
                  </p>
                  <div className="aspect-video rounded-xl overflow-hidden">
                    <img
                      src={selectedApp.image || `https://images.unsplash.com/photo-${
                        selectedApp.id === '1'
                          ? '1496181133206-80ce9b88a853'
                          : selectedApp.id === '2'
                          ? '1581091226825-a6a2a5aee158'
                          : selectedApp.id === '3'
                          ? '1504148451-1488729e17f8'
                          : selectedApp.id === '4'
                          ? '1557597774-9d273605dfa9'
                          : '1451187580459-43490279c0fa'
                      }?w=800&q=80`}
                      alt={selectedApp.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-6">典型应用产品</h3>
                  {relatedProducts.length > 0 ? (
                    <div className="space-y-4">
                      {relatedProducts.map((product) => (
                        <Link
                          key={product.id}
                          to={`/products/${product.id}`}
                          className="block p-4 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-bold text-lg">{product.model}</div>
                              <div className="text-sm text-gray-500">
                                {product.parameters.application}
                              </div>
                            </div>
                            <ArrowRight className="h-5 w-5 text-gray-400" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <p>该应用领域可使用多种产品，请浏览产品中心了解更多。</p>
                      <Link
                        to="/products"
                        className="inline-flex items-center mt-4 text-blue-600 font-medium"
                      >
                        浏览产品中心 <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
